import React, { Component } from "react";
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import {
  getTransportRequestById,
  getActiveTransportDrivers,
  acceptTransportRequest,
  rejectTransportRequest,
  assignTransportDriver,
  updateTransportRequestStatus,
  cancelTransportRequest,
  createTransportDriver,
  setTransportQuote,
  updateTransportPaymentStatus,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/Common/ConfirmModal";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { canWrite } from "../../services/PermissionService";
import { C, Panel, Chip, Btn, Avatar, MODAL_CSS } from "../../components/Console/ConsoleUI";

/**
 * One transport request — console screens D2 (assign), D3 (live stage) and D4
 * (close-out), which are the same record at three points in its life, so they
 * are one page that changes shape rather than three routes.
 *
 * A reskin: every call, every handler and every modal below is the one this
 * page already used — accept, reject, cancel, quote, assign driver, create and
 * assign a driver, mark payment received, advance status.
 *
 * What the handoff draws and this system cannot supply is left out rather than
 * faked: there is no live GPS feed, no per-minute ETA and no ETA history, so
 * D3's map, "arrives at" panel and delay controls are absent; the stage strip
 * is built from the request's own statusHistory instead. D4's driver payout
 * split and loyalty follow-ups are likewise not stored on a transport request.
 */

const CSS = MODAL_CSS;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_LABELS = {
  pending: "Pending review",
  accepted: "Accepted",
  rejected: "Rejected",
  driver_assigned: "Driver assigned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_TONE = {
  pending: "warn",
  accepted: "good",
  driver_assigned: "good",
  in_progress: "good",
  completed: "neutral",
  rejected: "bad",
  cancelled: "neutral",
};

const CATEGORY_LABELS = {
  protocol: "Protocol",
  transport: "Transport",
  protocol_transport: "Protocol & transport",
  day_rental: "Day rental",
  outstation: "Outstation",
  airport: "Airport transfer",
  intercity: "Inter-city",
};

const CATEGORY_ICON = {
  protocol: "concierge",
  transport: "local_taxi",
  protocol_transport: "workspace_premium",
  day_rental: "schedule",
  outstation: "route",
  airport: "flight_takeoff",
  intercity: "directions_car",
};

const SERVICE_TYPE_LABELS = {
  arrival: "Arrival",
  departure: "Departure",
  both: "Arrival & departure",
};

const TIER_LABELS = { standard: "Standard", comfort: "Comfort", vip: "VIP" };

const TRIP_TYPE_LABELS = {
  one_way: "One-way",
  round_trip: "Round trip",
  return_same_day: "Return same day",
};

const PAYMENT_METHODS = {
  cash: "Cash",
  card: "Card",
  room_charge: "Room charge",
  online: "Online",
};

const PAYMENT_LABELS = {
  paid: "Paid",
  refunded: "Refunded",
  waived: "Waived",
};

const PAYMENT_TONE = { paid: "good", refunded: "neutral", waived: "neutral" };

class TransportRequestDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      request: null,
      drivers: [],
      isLoaded: false,
      showDriverModal: false,
      showNewDriverModal: false,
      showQuoteModal: false,
      selectedDriverId: "",
      newDriver: {
        name: "",
        mobile: "",
        vehicleType: "sedan",
        vehicleNumber: "",
      },
      quoteData: {
        amount: "",
        perKmRate: "",
        estimatedKm: "",
        breakdown: "",
      },
      isSubmittingQuote: false,
      showPaymentModal: false,
      isUpdatingPayment: false,
      // Confirmation modal states
      showAcceptModal: false,
      showReasonModal: false,
      showPaymentConfirmModal: false,
      reasonModalType: "", // "reject" or "cancel"
      reasonText: "",
      actionLoading: false,
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport Requests", link: "/transport/requests" },
        { title: "Request Details", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadRequest();
    this.loadDrivers();
  }

  loadRequest = () => {
    const { id } = this.props.match.params;
    getTransportRequestById(id)
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ request: result.data, isLoaded: true });
        } else {
          toast.error(result?.message || "Failed to load request");
        }
      })
      .catch(() => toast.error("Error loading request"));
  };

  loadDrivers = () => {
    getActiveTransportDrivers()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ drivers: result.data });
        }
      })
      .catch(() => console.error("Error loading drivers"));
  };

  // Modal toggle handlers
  toggleAcceptModal = () => this.setState({ showAcceptModal: !this.state.showAcceptModal });
  toggleReasonModal = () => this.setState({ showReasonModal: !this.state.showReasonModal, reasonText: "" });
  togglePaymentConfirmModal = () => this.setState({ showPaymentConfirmModal: !this.state.showPaymentConfirmModal });

  handleAccept = () => {
    this.setState({ showAcceptModal: true });
  };

  confirmAccept = () => {
    const { request } = this.state;
    this.setState({ actionLoading: true });
    acceptTransportRequest(request._id)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Request accepted");
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to accept");
        }
      })
      .finally(() => {
        this.setState({ actionLoading: false, showAcceptModal: false });
      });
  };

  handleReject = () => {
    this.setState({ reasonModalType: "reject", showReasonModal: true });
  };

  confirmReasonAction = () => {
    const { request, reasonModalType, reasonText } = this.state;
    if (!reasonText.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    this.setState({ actionLoading: true });

    const apiCall = reasonModalType === "reject"
      ? rejectTransportRequest(request._id, reasonText)
      : cancelTransportRequest(request._id, reasonText);

    apiCall
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(reasonModalType === "reject" ? "Request rejected" : "Request cancelled");
          this.loadRequest();
        } else {
          toast.error(response?.message || `Failed to ${reasonModalType}`);
        }
      })
      .finally(() => {
        this.setState({
          actionLoading: false,
          showReasonModal: false,
          reasonText: "",
          reasonModalType: "",
        });
      });
  };

  handleSetQuote = () => {
    const { request, quoteData } = this.state;

    if (!quoteData.amount || parseFloat(quoteData.amount) <= 0) {
      toast.error("Please enter a valid quote amount");
      return;
    }

    this.setState({ isSubmittingQuote: true });

    setTransportQuote(request._id, {
      amount: parseFloat(quoteData.amount),
      perKmRate: quoteData.perKmRate ? parseFloat(quoteData.perKmRate) : null,
      estimatedKm: quoteData.estimatedKm ? parseFloat(quoteData.estimatedKm) : null,
      breakdown: quoteData.breakdown || null,
    })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Quote sent to customer. Awaiting response.");
          this.setState({
            showQuoteModal: false,
            quoteData: { amount: "", perKmRate: "", estimatedKm: "", breakdown: "" },
          });
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to set quote");
        }
      })
      .catch(() => toast.error("Error setting quote"))
      .finally(() => this.setState({ isSubmittingQuote: false }));
  };

  handleCancel = () => {
    this.setState({ reasonModalType: "cancel", showReasonModal: true });
  };

  handleMarkPaymentReceived = () => {
    this.setState({ showPaymentConfirmModal: true });
  };

  confirmMarkPaymentReceived = () => {
    const { request } = this.state;
    this.setState({ isUpdatingPayment: true, showPaymentConfirmModal: false });

    updateTransportPaymentStatus(request._id, {
      paymentStatus: "paid",
      notes: "Payment received and confirmed by admin",
    })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Payment marked as received");
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to update payment status");
        }
      })
      .catch(() => toast.error("Error updating payment status"))
      .finally(() => this.setState({ isUpdatingPayment: false }));
  };

  handleAssignDriver = () => {
    const { request, selectedDriverId, drivers } = this.state;

    if (!selectedDriverId) {
      toast.error("Please select a driver");
      return;
    }

    const driver = drivers.find((d) => d._id === selectedDriverId);

    assignTransportDriver(request._id, selectedDriverId, {
      name: driver.name,
      mobile: driver.mobile,
      vehicleNumber: driver.vehicleNumber,
    })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Driver assigned successfully");
          this.setState({ showDriverModal: false });
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to assign driver");
        }
      });
  };

  handleCreateAndAssignDriver = () => {
    const { request, newDriver } = this.state;

    if (!newDriver.name || !newDriver.mobile) {
      toast.error("Driver name and mobile are required");
      return;
    }

    // Create new driver first
    createTransportDriver(newDriver)
      .then((response) => {
        if (response && response.status === 201) {
          const createdDriver = response.data;
          // Now assign the driver
          assignTransportDriver(request._id, createdDriver._id, {
            name: createdDriver.name,
            mobile: createdDriver.mobile,
            vehicleNumber: createdDriver.vehicleNumber,
          })
            .then((assignResponse) => {
              if (assignResponse && assignResponse.status === 200) {
                toast.success("Driver created and assigned");
                this.setState({
                  showNewDriverModal: false,
                  showDriverModal: false,
                  newDriver: { name: "", mobile: "", vehicleType: "sedan", vehicleNumber: "" },
                });
                this.loadRequest();
                this.loadDrivers();
              }
            });
        } else {
          toast.error(response?.message || "Failed to create driver");
        }
      });
  };

  handleUpdateStatus = (newStatus) => {
    const { request } = this.state;
    const notes = prompt("Add notes (optional):");

    updateTransportRequestStatus(request._id, newStatus, notes || "")
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(`Status updated to ${newStatus}`);
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to update status");
        }
      });
  };

  formatDate = (date) => {
    // Returns null, not the string "N/A" — callers join and filter, and a
    // literal "N/A" in a subtitle reads as data rather than as absence.
    if (!date) return null;
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  /** Time only, for the stepper and timeline. */
  formatTime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d)) return null;
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  /** When a given status was reached, from the request's own history. */
  reachedAt = (status) => {
    const h = (this.state.request?.statusHistory || []).filter((x) => x.status === status);
    return h.length ? h[h.length - 1].changedAt : null;
  };

  /**
   * The ride, told as five stages. Which stage a request is on comes from its
   * status; when it got there comes from statusHistory, so a stage with no
   * recorded time is shown without one rather than with a guess.
   */
  stages = () => {
    const { request } = this.state;
    const protocolOnly = request.serviceCategory === "protocol";
    const defs = [
      { key: "pending", label: "Requested" },
      { key: "accepted", label: "Accepted" },
      ...(protocolOnly ? [] : [{ key: "driver_assigned", label: "Driver assigned" }]),
      { key: "in_progress", label: protocolOnly ? "Service under way" : "On the road" },
      { key: "completed", label: "Completed" },
    ];
    const order = defs.map((d) => d.key);
    // A cancelled or rejected ride never reaches the later stages; it stops
    // where it stopped, and the strip says so instead of pretending progress.
    const dead = ["cancelled", "rejected"].includes(request.status);
    const current = dead
      ? order.indexOf("accepted") >= 0 && this.reachedAt("accepted") ? 1 : 0
      : Math.max(0, order.indexOf(request.status));
    return defs.map((d, i) => ({
      ...d,
      at: this.formatTime(this.reachedAt(d.key)),
      state: i < current ? "done" : i === current ? (dead ? "stopped" : "current") : "todo",
    }));
  };

  render() {
    const {
      request, drivers, showDriverModal, showNewDriverModal, showQuoteModal,
      selectedDriverId, newDriver, quoteData, isSubmittingQuote,
    } = this.state;

    if (!request) {
      return (
        <ConsolePage title="Request" subtitle="Transport">
          <Panel>
            <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
              <Spinner style={{ color: C.brown }} />
              <div style={{ marginTop: 10 }}>Loading request…</div>
            </div>
          </Panel>
        </ConsolePage>
      );
    }

    const serviceCategory = request.serviceCategory || "airport";
    const requiresDriver = ["transport", "protocol_transport", "day_rental", "outstation", "airport", "intercity"]
      .includes(serviceCategory);
    const isProtocolOnly = serviceCategory === "protocol";

    const stage = STATUS_LABELS[request.status] || request.status;
    const tone = STATUS_TONE[request.status] || "neutral";
    const isDone = request.status === "completed";
    const fare = request.quotedPrice?.amount > 0
      ? request.quotedPrice.amount
      : request.pricing?.totalPrice || 0;
    const currency = request.pricing?.currency || "USD";

    const steps = this.stages();
    const done = steps.filter((s) => s.state === "done").length;
    const dead = ["cancelled", "rejected"].includes(request.status);

    const facts = [
      {
        label: "Guest",
        value: request.contactName || request.userId?.name || "—",
      },
      {
        label: "Party",
        value: `${request.passengers || 1} passenger${(request.passengers || 1) === 1 ? "" : "s"}, ${request.luggage || 0} bag${(request.luggage || 0) === 1 ? "" : "s"}`,
      },
      {
        label: request.quotedPrice?.amount > 0 ? "Quoted fare" : "Fare",
        value: `${money(fare)} · ${currency}`,
        strong: true,
      },
    ];

    return (
      <ConsolePage
        title={`${CATEGORY_LABELS[serviceCategory] || serviceCategory} · ${request.requestNumber}`}
        subtitle={`Transport request · ${stage}`}
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/transport/requests")}>
              All requests
            </Btn>

            {/* Every control below writes to the request. A view-only account
                still reads the whole record; it just gets no actions. */}
            {canWrite() && request.status === "pending" && request.quoteStatus === "pending_quote" && (
              <React.Fragment>
                <Btn kind="quiet" icon="close" onClick={this.handleReject}>Reject</Btn>
                <Btn icon="sell" onClick={() => this.setState({ showQuoteModal: true })}>Set quote</Btn>
              </React.Fragment>
            )}

            {/* Quote sent, guest has not answered */}
            {canWrite() && request.status === "pending" && request.quoteStatus === "quoted" && (
              <React.Fragment>
                <Chip tone="warn">Waiting on the guest</Chip>
                <Btn kind="quiet" icon="edit" onClick={() => this.setState({ showQuoteModal: true })}>
                  Modify quote
                </Btn>
              </React.Fragment>
            )}

            {/* Everything else pending */}
            {canWrite() && request.status === "pending" &&
              (!request.quoteStatus || request.quoteStatus === "not_applicable" || request.quoteStatus === "accepted") && (
              <React.Fragment>
                <Btn kind="quiet" icon="close" onClick={this.handleReject}>Reject</Btn>
                <Btn icon="check" onClick={this.handleAccept}>Accept</Btn>
              </React.Fragment>
            )}

            {canWrite() && request.status === "accepted" && requiresDriver && (
              <Btn icon="person_add" onClick={() => this.setState({ showDriverModal: true })}>
                Assign driver
              </Btn>
            )}
            {canWrite() && request.status === "accepted" && isProtocolOnly && (
              <Btn icon="play_arrow" onClick={() => this.handleUpdateStatus("in_progress")}>
                Start service
              </Btn>
            )}
            {canWrite() && request.status === "driver_assigned" && (
              <Btn kind="quiet" icon="swap_horiz" onClick={() => this.setState({ showDriverModal: true })}>
                Change driver
              </Btn>
            )}
            {canWrite() && ["pending", "accepted", "driver_assigned"].includes(request.status) && (
              <Btn kind="quiet" icon="block" onClick={this.handleCancel}>Cancel</Btn>
            )}
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 400px)", gap: 20, alignItems: "start" }}>
          {/* ---------------------------------------------------------- left */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
            {isDone ? (
              // The design's close-out banner: the ride is finished, and the
              // two things anyone still wants are when and how much.
              <div
                style={{
                  background: C.brown, borderRadius: 18, padding: 26, color: "#fff",
                  display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
                }}
              >
                <span
                  className="ms"
                  style={{ fontSize: 30, background: C.green, padding: 14, borderRadius: 18 }}
                >
                  check
                </span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 22, fontWeight: 500 }}>
                    {isProtocolOnly ? "Service completed" : "Ride completed"}
                  </div>
                  <div style={{ marginTop: 5, fontSize: 15, color: C.sand }}>
                    {this.reachedAt("completed")
                      ? [this.formatDate(this.reachedAt("completed")), this.formatTime(this.reachedAt("completed"))]
                          .filter(Boolean).join(" · ")
                      : "Closed out"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,.6)" }}>TOTAL</div>
                  <div style={{ marginTop: 4, fontSize: 30, fontWeight: 500 }}>{money(fare)}</div>
                </div>
              </div>
            ) : (
              <Panel>
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <span
                    className="ms"
                    style={{ fontSize: 24, color: C.brown, background: C.iconTint, padding: 11, borderRadius: 14 }}
                  >
                    {CATEGORY_ICON[serviceCategory] || "local_taxi"}
                  </span>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 18, fontWeight: 500, color: C.ink }}>
                      {CATEGORY_LABELS[serviceCategory] || serviceCategory}
                      {request.serviceType ? ` · ${SERVICE_TYPE_LABELS[request.serviceType]}` : ""}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                      Raised {this.formatDate(request.createdAt)}
                      {request.tierType ? ` · ${TIER_LABELS[request.tierType] || request.tierType} class` : ""}
                      {serviceCategory === "day_rental" && request.rentalHours ? ` · ${request.rentalHours} h` : ""}
                      {serviceCategory === "outstation" && request.tripType
                        ? ` · ${TRIP_TYPE_LABELS[request.tripType] || request.tripType}`
                        : ""}
                    </div>
                  </div>
                  <Chip tone={tone}>{stage}</Chip>
                </div>

                <div
                  style={{
                    marginTop: 20, display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)", gap: 16,
                  }}
                >
                  {facts.map((f) => (
                    <div key={f.label}>
                      <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupeLight }}>
                        {f.label}
                      </div>
                      <div
                        style={{
                          marginTop: 5, fontSize: 15, color: C.ink,
                          fontWeight: f.strong ? 500 : 400,
                        }}
                      >
                        {f.value}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {/* Stage strip */}
            <Panel title="Ride stage" subtitle="Where this request has got to">
              <div
                style={{
                  marginTop: 22, position: "relative",
                  display: "grid", gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
                }}
              >
                {/* The rail the dots sit on, and the filled part behind the
                    stages already passed. Drawn under the dots, never over. */}
                <div
                  style={{
                    position: "absolute", top: 15, height: 3,
                    left: `${50 / steps.length}%`, right: `${50 / steps.length}%`,
                    background: C.tint,
                  }}
                />
                <div
                  style={{
                    position: "absolute", top: 15, height: 3,
                    left: `${50 / steps.length}%`,
                    width: `${(100 - 100 / steps.length) * (done / Math.max(1, steps.length - 1))}%`,
                    background: dead ? C.red : C.green,
                  }}
                />
                {steps.map((s, i) => (
                  <div
                    key={s.key}
                    style={{
                      position: "relative", display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 10, textAlign: "center",
                    }}
                  >
                    <StageDot state={s.state} isLast={i === steps.length - 1} />
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: s.state === "current" ? 500 : 400,
                          color: s.state === "todo" ? C.taupeLight : C.ink,
                        }}
                      >
                        {s.label}
                      </div>
                      <div style={{ fontSize: 12, color: C.taupeLight }}>
                        {s.at || (s.state === "todo" ? "—" : "time not recorded")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {["cancelled", "rejected"].includes(request.status) && (
                <div
                  style={{
                    marginTop: 18, padding: "12px 15px", borderRadius: 12,
                    background: "rgba(192,32,43,.08)", fontSize: 14, color: C.red,
                  }}
                >
                  This request was {request.status}. It will not go any further.
                </div>
              )}
            </Panel>

            {/* Pickup / arrival */}
            {(request.services?.pickup ||
              (["arrival", "both"].includes(request.serviceType) && request.pickupDetails)) &&
              request.pickupDetails && (
              <Panel
                title={isProtocolOnly ? "Arrival details" : "Pickup details"}
                subtitle={[this.formatDate(request.pickupDetails.date), request.pickupDetails.time]
                  .filter(Boolean).join(" · ") || null}
              >
                <DefGrid
                  items={[
                    { label: "Location", value: request.pickupDetails.location || "Airport" },
                    { label: "Drop-off", value: request.pickupDetails.dropoffLocation },
                    { label: "Flight", value: request.pickupDetails.flightNumber },
                    { label: "Airline", value: request.pickupDetails.airline },
                    { label: "Terminal", value: request.pickupDetails.terminal },
                    { label: "Lands at", value: request.pickupDetails.arrivalTime },
                  ]}
                />
              </Panel>
            )}

            {/* Drop / departure */}
            {(request.services?.drop ||
              (["departure", "both"].includes(request.serviceType) && request.dropDetails)) &&
              request.dropDetails && (
              <Panel
                title={isProtocolOnly ? "Departure details" : "Drop details"}
                subtitle={[this.formatDate(request.dropDetails.date), request.dropDetails.time]
                  .filter(Boolean).join(" · ") || null}
              >
                <DefGrid
                  items={[
                    { label: "Collect from", value: request.dropDetails.pickupLocation || request.dropDetails.location },
                    { label: "Drop at", value: request.dropDetails.location || "Airport" },
                    { label: "Flight", value: request.dropDetails.flightNumber },
                    { label: "Airline", value: request.dropDetails.airline },
                    { label: "Terminal", value: request.dropDetails.terminal },
                    { label: "Departs", value: request.dropDetails.departureTime },
                  ]}
                />
              </Panel>
            )}

            {/* Day rental */}
            {serviceCategory === "day_rental" && request.dayRentalDetails && (
              <Panel
                title="Day rental"
                subtitle={[this.formatDate(request.dayRentalDetails.date), request.dayRentalDetails.time]
                  .filter(Boolean).join(" · ") || null}
              >
                <DefGrid
                  items={[
                    { label: "Duration", value: request.dayRentalDetails.duration ? `${request.dayRentalDetails.duration} hours` : null },
                    { label: "Pickup address", value: request.dayRentalDetails.address },
                    { label: "Vehicle preference", value: request.vehicleTypePreference },
                  ]}
                />
                {request.dayRentalDetails.coordinates?.lat && request.dayRentalDetails.coordinates?.lng && (
                  <MiniMap
                    label="Pickup location"
                    lat={request.dayRentalDetails.coordinates.lat}
                    lng={request.dayRentalDetails.coordinates.lng}
                    height={220}
                  />
                )}
              </Panel>
            )}

            {/* Outstation */}
            {serviceCategory === "outstation" && request.outstationDetails && (
              <Panel
                title="Outstation"
                subtitle={[
                  TRIP_TYPE_LABELS[request.journeyType] || request.journeyType,
                  this.formatDate(request.outstationDetails.date),
                  request.outstationDetails.time,
                ].filter(Boolean).join(" · ") || null}
              >
                <DefGrid
                  items={[
                    { label: "From", value: request.outstationDetails.pickupLocation },
                    { label: "To", value: request.outstationDetails.dropLocation },
                    { label: "Start / end address", value: request.outstationDetails.address },
                    { label: "Visiting", value: request.outstationDetails.visitingPlace },
                    { label: "Vehicle preference", value: request.vehicleTypePreference },
                  ]}
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {(request.outstationDetails.pickupCoordinates?.lat ||
                    request.outstationDetails.addressCoordinates?.lat) && (
                    <MiniMap
                      label="Pickup"
                      lat={request.outstationDetails.pickupCoordinates?.lat || request.outstationDetails.addressCoordinates?.lat}
                      lng={request.outstationDetails.pickupCoordinates?.lng || request.outstationDetails.addressCoordinates?.lng}
                    />
                  )}
                  {(request.outstationDetails.dropCoordinates?.lat ||
                    request.outstationDetails.visitingPlaceCoordinates?.lat) && (
                    <MiniMap
                      label={request.outstationDetails.dropCoordinates?.lat ? "Drop" : "Visiting place"}
                      lat={request.outstationDetails.dropCoordinates?.lat || request.outstationDetails.visitingPlaceCoordinates?.lat}
                      lng={request.outstationDetails.dropCoordinates?.lng || request.outstationDetails.visitingPlaceCoordinates?.lng}
                    />
                  )}
                </div>
              </Panel>
            )}

            {/* Protocol */}
            {(serviceCategory === "protocol" || serviceCategory === "protocol_transport") && request.visaType && (
              <Panel title="Protocol">
                <DefGrid
                  items={[
                    { label: "Visa type", value: request.visaType },
                    { label: "Nationality", value: request.nationality },
                    { label: "Notes", value: request.protocolNotes },
                  ]}
                />
              </Panel>
            )}

            {request.specialRequests && (
              <Panel title="Special requests">
                <div style={{ marginTop: 12, fontSize: 14, color: C.slate, lineHeight: 1.55 }}>
                  {request.specialRequests}
                </div>
              </Panel>
            )}

            {/* Trip timeline — the design's close-out history */}
            {request.statusHistory && request.statusHistory.length > 0 && (
              <Panel title="Trip timeline" subtitle="Every stage this request passed through">
                <div style={{ marginTop: 18 }}>
                  {request.statusHistory.map((h, idx) => {
                    const last = idx === request.statusHistory.length - 1;
                    return (
                      <div key={idx} style={{ display: "flex", gap: 16 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <span
                            style={{
                              width: 13, height: 13, borderRadius: 7,
                              background: C.brown, marginTop: 5, flex: "0 0 13px",
                            }}
                          />
                          {!last && <span style={{ flex: 1, width: 2, background: C.border }} />}
                        </div>
                        <div style={{ paddingBottom: last ? 0 : 18 }}>
                          <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>
                            {STATUS_LABELS[h.status] || h.status}
                            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 400, color: C.taupeLight }}>
                              {new Date(h.changedAt).toLocaleString()}
                            </span>
                          </div>
                          {h.notes && (
                            <div style={{ marginTop: 2, fontSize: 13, color: C.taupe }}>{h.notes}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            )}
          </div>

          {/* --------------------------------------------------------- right */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
            <Panel title="Guest">
              <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 13 }}>
                <Avatar name={request.contactName || request.userId?.name} size={46} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, color: C.ink }}>
                    {request.contactName || request.userId?.name || "—"}
                  </div>
                  <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight, wordBreak: "break-all" }}>
                    {request.contactEmail || request.userId?.email || "no email on file"}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Tile label="Phone" value={request.contactMobile || request.userId?.mobileNo || "—"} />
                <Tile label="Passengers" value={request.passengers || 1} />
                <Tile label="Luggage" value={request.luggage || 0} />
                <Tile label="Vehicle" value={request.vehicleTypeId?.name || request.vehicleTypeName || "—"} />
              </div>
            </Panel>

            <Panel title="Driver & vehicle">
              {request.assignedDriver ? (
                <React.Fragment>
                  <div
                    style={{
                      marginTop: 14, display: "flex", alignItems: "center", gap: 13,
                      padding: 13, borderRadius: 14, background: C.surface,
                    }}
                  >
                    <Avatar name={request.assignedDriver.name} size={42} solid />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, color: C.ink }}>{request.assignedDriver.name}</div>
                      <div style={{ marginTop: 3, fontSize: 13, color: C.taupe }}>
                        {request.assignedDriver.mobile}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <Tile label="Plate" value={request.assignedDriver.vehicleNumber || "—"} />
                    <Tile label="Assigned" value={this.formatDate(request.driverAssignedAt) || "—"} />
                  </div>
                </React.Fragment>
              ) : (
                <div style={{ marginTop: 14, fontSize: 14, color: C.taupeLight }}>
                  {requiresDriver
                    ? "No driver assigned yet."
                    : "This service does not need a driver."}
                </div>
              )}
            </Panel>

            {/* Fare */}
            <Panel title={request.quotedPrice?.amount > 0 ? "Quote" : "Fare"}>
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 11, fontSize: 14, color: C.slate }}>
                {request.quotedPrice?.amount > 0 ? (
                  <React.Fragment>
                    <FareRow label="Quoted amount" value={money(request.quotedPrice.amount)} />
                    {request.quotedPrice.estimatedKm > 0 && (
                      <FareRow label="Estimated distance" value={`${request.quotedPrice.estimatedKm} km`} />
                    )}
                    {request.quotedPrice.perKmRate > 0 && (
                      <FareRow label="Per km" value={`${money(request.quotedPrice.perKmRate)}/km`} />
                    )}
                    {request.quotedPrice.quotedAt && (
                      <FareRow label="Quoted" value={new Date(request.quotedPrice.quotedAt).toLocaleString()} />
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {request.pricing?.pickupPrice > 0 && (
                      <FareRow label="Airport pickup" value={money(request.pricing.pickupPrice)} />
                    )}
                    {request.pricing?.dropPrice > 0 && (
                      <FareRow label="Airport drop" value={money(request.pricing.dropPrice)} />
                    )}
                    {request.pricing?.meetAndGreetPrice > 0 && (
                      <FareRow
                        label="Meet & greet"
                        value={request.pricing.isMeetGreetFree ? "Free" : money(request.pricing.meetAndGreetPrice)}
                        good={request.pricing.isMeetGreetFree}
                      />
                    )}
                    {/* A lone "$0.00 total" reads as a free ride. Say why it is
                        zero when the record simply has no price on it yet. */}
                    {!(request.pricing?.pickupPrice > 0) &&
                      !(request.pricing?.dropPrice > 0) &&
                      !(request.pricing?.meetAndGreetPrice > 0) && (
                      <div style={{ fontSize: 14, color: C.taupeLight }}>
                        {request.quoteStatus === "pending_quote"
                          ? "No quote set yet — this ride is priced by hand."
                          : "No price breakdown recorded."}
                      </div>
                    )}
                  </React.Fragment>
                )}
                <div
                  style={{
                    marginTop: 4, paddingTop: 12, borderTop: `1px solid ${C.tint}`,
                    display: "flex", justifyContent: "space-between",
                    fontSize: 17, fontWeight: 500, color: C.ink,
                  }}
                >
                  <span>Total</span>
                  <span>{money(fare)}</span>
                </div>
              </div>

              {request.quotedPrice?.breakdown && (
                <div
                  style={{
                    marginTop: 14, padding: "13px 15px", borderRadius: 12, background: C.surface,
                    fontSize: 13, color: C.slate, whiteSpace: "pre-wrap", lineHeight: 1.5,
                  }}
                >
                  {request.quotedPrice.breakdown}
                </div>
              )}
              {request.quoteStatus === "quoted" && (
                <div style={{ marginTop: 12 }}>
                  <Chip tone="warn">Waiting for the guest to accept</Chip>
                </div>
              )}
              {request.quoteStatus === "accepted" && (
                <div style={{ marginTop: 12 }}><Chip tone="good">Quote accepted</Chip></div>
              )}
              {request.quoteStatus === "rejected" && (
                <div
                  style={{
                    marginTop: 12, padding: "12px 15px", borderRadius: 12,
                    background: "rgba(192,32,43,.08)", fontSize: 13, color: C.red,
                  }}
                >
                  Quote rejected
                  {request.quoteResponse?.rejectionReason ? ` — ${request.quoteResponse.rejectionReason}` : ""}
                </div>
              )}
            </Panel>

            {/* Settlement */}
            <Panel title="Payment">
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 11 }}>
                <FareRow label="Method" value={PAYMENT_METHODS[request.paymentMethod] || request.paymentMethod || "Not specified"} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 14, color: C.slate }}>Status</span>
                  <Chip tone={PAYMENT_TONE[request.paymentStatus] || "warn"}>
                    {PAYMENT_LABELS[request.paymentStatus] || "Pending"}
                  </Chip>
                </div>
                {/* Loyalty points; older requests carry none. */}
                {request.pointsRedeemed > 0 && (
                  <FareRow label="Points redeemed" value={`${Number(request.pointsRedeemed).toLocaleString()} pts`} />
                )}
                {request.pointsEarned > 0 && (
                  <FareRow label="Points earned" value={`+${Number(request.pointsEarned).toLocaleString()} pts`} good />
                )}
              </div>
              {canWrite() &&
                request.paymentMethod === "cash" &&
                request.paymentStatus !== "paid" &&
                ["driver_assigned", "in_progress", "completed"].includes(request.status) && (
                <div style={{ marginTop: 16 }}>
                  <Btn
                    icon="check"
                    onClick={this.handleMarkPaymentReceived}
                    disabled={this.state.isUpdatingPayment}
                    style={{ width: "100%" }}
                  >
                    {this.state.isUpdatingPayment ? "Updating…" : "Mark payment received"}
                  </Btn>
                </div>
              )}
            </Panel>

            {/* Feedback */}
            {request.feedback && request.feedback.rating && (
              <Panel title="Guest feedback">
                <div style={{ marginTop: 14 }}>
                  <Stars value={request.feedback.rating} label="Overall" />
                  {request.feedback.driverRating && (
                    <div style={{ marginTop: 10 }}>
                      <Stars value={request.feedback.driverRating} label="Driver" />
                    </div>
                  )}
                  {request.feedback.comment && (
                    <div style={{ marginTop: 12, fontSize: 14, color: C.slate, lineHeight: 1.5 }}>
                      “{request.feedback.comment}”
                    </div>
                  )}
                </div>
              </Panel>
            )}
          </div>
        </div>

        {/* Assign Driver Modal */}
        <Modal
          isOpen={showDriverModal}
          toggle={() => this.setState({ showDriverModal: false })}
          className="con-modal"
          centered
        >
          <ModalHeader toggle={() => this.setState({ showDriverModal: false })}>
            Assign a driver
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Select existing driver</Label>
              <Input
                type="select"
                value={selectedDriverId}
                onChange={(e) => this.setState({ selectedDriverId: e.target.value })}
              >
                <option value="">-- Select Driver --</option>
                {drivers.map((driver) => (
                  <option key={driver._id} value={driver._id}>
                    {driver.name} - {driver.mobile} {driver.vehicleNumber ? `(${driver.vehicleNumber})` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <div style={{ textAlign: "center", margin: "18px 0", fontSize: 13, color: C.taupeLight }}>
              or
            </div>
            <Btn
              kind="quiet"
              icon="add"
              onClick={() => this.setState({ showNewDriverModal: true, showDriverModal: false })}
              style={{ width: "100%" }}
            >
              Create a new driver
            </Btn>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showDriverModal: false })}>
              Cancel
            </Btn>
            <Btn onClick={this.handleAssignDriver} disabled={!selectedDriverId}>
              Assign driver
            </Btn>
          </ModalFooter>
        </Modal>

        {/* New Driver Modal */}
        <Modal
          isOpen={showNewDriverModal}
          toggle={() => this.setState({ showNewDriverModal: false })}
          className="con-modal"
          centered
        >
          <ModalHeader toggle={() => this.setState({ showNewDriverModal: false })}>
            Create &amp; assign a driver
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Driver Name *</Label>
              <Input
                type="text"
                value={newDriver.name}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, name: e.target.value } })}
                placeholder="Enter driver name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Mobile Number *</Label>
              <Input
                type="text"
                value={newDriver.mobile}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, mobile: e.target.value } })}
                placeholder="Enter mobile number"
              />
            </FormGroup>
            <FormGroup>
              <Label>Vehicle Type</Label>
              <Input
                type="select"
                value={newDriver.vehicleType}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, vehicleType: e.target.value } })}
              >
                <option value="sedan">Sedan</option>
                <option value="suv">SUV</option>
                <option value="van">Van</option>
                <option value="bus">Bus</option>
                <option value="other">Other</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Vehicle Number</Label>
              <Input
                type="text"
                value={newDriver.vehicleNumber}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, vehicleNumber: e.target.value } })}
                placeholder="Enter vehicle number (optional)"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Btn
              kind="quiet"
              onClick={() => this.setState({ showNewDriverModal: false, showDriverModal: true })}
            >
              Back
            </Btn>
            <Btn onClick={this.handleCreateAndAssignDriver}>
              Create &amp; assign
            </Btn>
          </ModalFooter>
        </Modal>

        {/* Set Quote Modal (for day_rental and outstation services) */}
        <Modal
          isOpen={showQuoteModal}
          toggle={() => this.setState({ showQuoteModal: false })}
          className="con-modal"
          centered
        >
          <ModalHeader toggle={() => this.setState({ showQuoteModal: false })}>
            Set a price quote
          </ModalHeader>
          <ModalBody>
            {request?.vehicleTypePreference && request.vehicleTypePreference !== "any" && (
              <div
                style={{
                  marginBottom: 16, padding: "12px 15px", borderRadius: 12,
                  background: C.iconTint, fontSize: 13, color: C.slate,
                }}
              >
                The guest asked for a{" "}
                <strong>
                  {request.vehicleTypePreference.charAt(0).toUpperCase() +
                    request.vehicleTypePreference.slice(1)}
                </strong>
                .
              </div>
            )}
            {request && (
              <div
                style={{
                  marginBottom: 18, padding: "14px 16px", borderRadius: 12,
                  background: C.surface, border: `1px solid ${C.border}`,
                  fontSize: 13, color: C.slate, lineHeight: 1.6,
                }}
              >
                <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupeLight, marginBottom: 6 }}>
                  What is being quoted
                </div>
                <div>
                  <strong>Service:</strong>{" "}
                  {request.serviceCategory === "day_rental" ? "Day rental" : "Outstation"}
                </div>
                {request.dayRentalDetails && (
                  <React.Fragment>
                    <div><strong>Duration:</strong> {request.dayRentalDetails.duration || "N/A"} hours</div>
                    <div><strong>Pickup:</strong> {request.dayRentalDetails.address || "N/A"}</div>
                  </React.Fragment>
                )}
                {request.outstationDetails && (
                  <React.Fragment>
                    <div>
                      <strong>Journey:</strong>{" "}
                      {request.outstationDetails.pickupLocation || request.outstationDetails.address || "N/A"} →{" "}
                      {request.outstationDetails.dropLocation || request.outstationDetails.visitingPlace || "N/A"}
                    </div>
                    <div><strong>Journey type:</strong> {request.journeyType || "N/A"}</div>
                    {request.tripDetails?.estimatedKm && (
                      <div><strong>Est. distance:</strong> {request.tripDetails.estimatedKm} km</div>
                    )}
                  </React.Fragment>
                )}
              </div>
            )}
            <FormGroup>
              <Label>Quote Amount (USD) *</Label>
              <Input
                type="number"
                value={quoteData.amount}
                onChange={(e) => this.setState({ quoteData: { ...quoteData, amount: e.target.value } })}
                placeholder="Enter total quote amount"
                min="0"
                step="0.01"
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Per KM Rate (Optional)</Label>
                  <Input
                    type="number"
                    value={quoteData.perKmRate}
                    onChange={(e) => this.setState({ quoteData: { ...quoteData, perKmRate: e.target.value } })}
                    placeholder="Rate per km"
                    min="0"
                    step="0.01"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Estimated KM (Optional)</Label>
                  <Input
                    type="number"
                    value={quoteData.estimatedKm}
                    onChange={(e) => this.setState({ quoteData: { ...quoteData, estimatedKm: e.target.value } })}
                    placeholder="Estimated kilometers"
                    min="0"
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Quote Breakdown (Optional)</Label>
              <Input
                type="textarea"
                value={quoteData.breakdown}
                onChange={(e) => this.setState({ quoteData: { ...quoteData, breakdown: e.target.value } })}
                placeholder="E.g., Base fare: $50, Fuel: $30, Driver allowance: $20"
                rows="3"
              />
              <small style={{ color: C.taupeLight }}>The guest sees this.</small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showQuoteModal: false })}>
              Cancel
            </Btn>
            <Btn
              onClick={this.handleSetQuote}
              disabled={isSubmittingQuote || !quoteData.amount}
            >
              {isSubmittingQuote ? "Sending…" : "Send quote to guest"}
            </Btn>
          </ModalFooter>
        </Modal>

        {/* Accept Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showAcceptModal}
          toggle={this.toggleAcceptModal}
          onConfirm={this.confirmAccept}
          title="Accept Transport Request"
          message="Are you sure you want to accept this transport request?"
          confirmText="Accept"
          confirmColor="success"
          icon="ri-checkbox-circle-line"
          loading={this.state.actionLoading}
        />

        {/* Payment Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showPaymentConfirmModal}
          toggle={this.togglePaymentConfirmModal}
          onConfirm={this.confirmMarkPaymentReceived}
          title="Confirm Payment"
          message="Mark payment as received for this booking?"
          confirmText="Confirm Payment"
          confirmColor="success"
          icon="ri-money-dollar-circle-line"
          loading={this.state.isUpdatingPayment}
        />

        {/* Reason Input Modal (for Reject/Cancel) */}
        <Modal
          isOpen={this.state.showReasonModal}
          toggle={this.toggleReasonModal}
          centered
          className="con-modal"
        >
          <ModalHeader toggle={this.toggleReasonModal}>
            {this.state.reasonModalType === "reject" ? "Reject request" : "Cancel request"}
          </ModalHeader>
          <ModalBody>
            <div style={{ fontSize: 14, color: C.taupe, marginBottom: 14 }}>
              The guest sees this reason, so write it as you would say it to them.
            </div>
            <FormGroup>
              <Label>Reason</Label>
              <Input
                type="textarea"
                rows="3"
                value={this.state.reasonText}
                onChange={(e) => this.setState({ reasonText: e.target.value })}
                placeholder={this.state.reasonModalType === "reject"
                  ? "Enter rejection reason..."
                  : "Enter cancellation reason..."}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={this.toggleReasonModal} disabled={this.state.actionLoading}>
              Keep it open
            </Btn>
            <Btn
              kind="danger"
              onClick={this.confirmReasonAction}
              disabled={this.state.actionLoading}
            >
              {this.state.actionLoading
                ? "Processing…"
                : this.state.reasonModalType === "reject" ? "Reject" : "Cancel request"}
            </Btn>
          </ModalFooter>
        </Modal>
      </ConsolePage>
    );
  }
}

/** One dot on the stage strip. Its fill says done / here / not yet. */
const StageDot = ({ state, isLast }) => {
  if (state === "done") {
    return (
      <span
        style={{
          width: 32, height: 32, borderRadius: 16, background: C.green,
          color: "#fff", display: "grid", placeItems: "center",
        }}
      >
        <span className="ms" style={{ fontSize: 19 }}>check</span>
      </span>
    );
  }
  if (state === "current" || state === "stopped") {
    const ring = state === "stopped" ? C.red : C.green;
    return (
      <span
        style={{
          width: 32, height: 32, borderRadius: 16, background: "#fff",
          border: `3px solid ${ring}`, display: "grid", placeItems: "center",
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: 5, background: ring }} />
      </span>
    );
  }
  return (
    <span
      style={{
        width: 32, height: 32, borderRadius: 16, background: "#f7f0e4",
        border: `2px solid ${C.border}`, display: "grid", placeItems: "center",
      }}
    >
      {isLast ? (
        <span className="ms" style={{ fontSize: 18, color: "#bfae99" }}>flag</span>
      ) : (
        <span style={{ width: 8, height: 8, borderRadius: 4, background: "#dccdb6" }} />
      )}
    </span>
  );
};

/** Label-over-value pairs, skipping anything the record does not carry. */
const DefGrid = ({ items }) => {
  const present = items.filter((i) => i.value !== undefined && i.value !== null && i.value !== "");
  if (present.length === 0) {
    return <div style={{ marginTop: 14, fontSize: 14, color: C.taupeLight }}>Nothing recorded.</div>;
  }
  return (
    <div
      style={{
        marginTop: 18, display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16,
      }}
    >
      {present.map((i) => (
        <div key={i.label}>
          <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupeLight }}>
            {i.label}
          </div>
          <div style={{ marginTop: 5, fontSize: 15, color: C.ink }}>{i.value}</div>
        </div>
      ))}
    </div>
  );
};

/** Small tinted fact tile, the design's side-panel unit. */
const Tile = ({ label, value }) => (
  <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface, minWidth: 0 }}>
    <div style={{ fontSize: 12, color: C.taupeLight }}>{label}</div>
    <div style={{ marginTop: 4, fontSize: 15, color: C.ink, wordBreak: "break-word" }}>{value}</div>
  </div>
);

const FareRow = ({ label, value, good }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14 }}>
    <span style={{ color: C.slate }}>{label}</span>
    <span style={{ color: good ? "#2f7d33" : C.ink, textAlign: "right" }}>{value}</span>
  </div>
);

const Stars = ({ value, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <span style={{ fontSize: 13, color: C.taupe, minWidth: 52 }}>{label}</span>
    <span style={{ display: "flex", gap: 2 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="ms"
          style={{ fontSize: 20, color: i < value ? C.bronze : "#e0d3bf" }}
        >
          star
        </span>
      ))}
    </span>
  </div>
);

/**
 * OpenStreetMap embed. Kept as an iframe because that is what this page has
 * always used — no key, no new dependency — but now titled, so a screen reader
 * is told which location it is looking at.
 */
const MiniMap = ({ label, lat, lng, height = 170 }) => (
  <div style={{ marginTop: 18 }}>
    <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupeLight, marginBottom: 7 }}>
      {label}
    </div>
    <div style={{ height, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.tint}` }}>
      <iframe
        title={`Map of ${label}`}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        loading="lazy"
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
      />
    </div>
    <a
      href={`https://www.google.com/maps?q=${lat},${lng}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, marginTop: 9,
        fontSize: 13, color: C.brown,
      }}
    >
      <span className="ms" style={{ fontSize: 16 }}>open_in_new</span>
      Open in Google Maps
    </a>
  </div>
);

export default TransportRequestDetail;
